import * as Sentry from '@sentry/react';
import { app } from 'store';
import store from 'store/store';
import { DocumentType } from 'types';
import { getCachedFlagValue } from 'utils/getFlagValue/getFlagValue';

export type WebViewerErrorKind = 'init' | 'load' | 'crash';

// Substrings pointing at an exhausted WASM heap. The engine heap is growable to 2GB
// (`_emscripten_get_heap_max` in core/pdf/full/PDFNetCWasm.js), and a failed grow is swallowed
// at the grow site, so exhaustion only ever surfaces later as one of these.
// Never add a bare 'oom' here — 'zoom' appears throughout the bundle.
const MEMORY_SIGNATURES = ['memory access out of bounds', 'out of memory', 'allocation failed'];

// Other traps that identify an error as coming from the PDFTron/WASM engine rather than
// ordinary app code. Unlike the memory ones these are NOT necessarily fatal — PDFTron
// frequently recovers and keeps rendering — so they only reach a toast behind the flag.
// 'aborted(' rather than 'abort(': the engine rewrites abort(what) to `Aborted(<what>)`.
const OTHER_ENGINE_SIGNATURES = ['unreachable', 'runtimeerror', 'aborted('];

const matchesAny = (message: string | undefined | null, signatures: string[]): boolean => {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return signatures.some((signature) => normalized.includes(signature));
};

/** True when an error message looks like it came from the PDFTron/WASM engine. */
export const isWebViewerEngineError = (message: string | undefined | null): boolean =>
  matchesAny(message, MEMORY_SIGNATURES) || matchesAny(message, OTHER_ENGINE_SIGNATURES);

/** True when an engine error points at an exhausted WASM heap. Always requires a reload. */
export const isMemoryError = (message: string | undefined | null): boolean => matchesAny(message, MEMORY_SIGNATURES);

const MESSAGES: Record<Exclude<WebViewerErrorKind, 'crash'>, string> = {
  init: 'The document viewer failed to start. Please reload the page.',
  load: 'This document could not be opened. Please reload the page.',
};

const FULL_ERROR_TOASTS_FLAG = 'fullWebviewerErrorToastsEnabled';

const isFullErrorToastsEnabled = (): boolean => {
  // Reading throws until App.tsx registers the LD client, and an init failure can fire before
  // that. Falling back to memory-only keeps the default behaviour rather than the widened one.
  try {
    return getCachedFlagValue(FULL_ERROR_TOASTS_FLAG);
  } catch {
    return false;
  }
};

const CRASH_PREFIX = 'The document viewer reported an error:';

// An exhausted heap is always the same unrecoverable situation, so it gets fixed copy rather than
// the trap text. A constant string also means every OOM collapses into one toast via the dedupe
// below and the duplicate check in `setSnackMessage`.
const MEMORY_CRASH_MESSAGE = 'Error: Out of memory. Please reload your page to continue.';

const MAX_DETAIL_LENGTH = 120;

/** Best available description of a failure, collapsed to a single line and never truncated. */
const describeError = (error: unknown, message?: string): string => {
  const raw = message || (error instanceof Error ? error.message : String(error ?? ''));
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (!collapsed || collapsed === '[object Object]') return 'unknown error';
  return collapsed;
};

// Engine errors can carry multi-line stack dumps. Only the toast is shortened — Sentry keeps
// the full text though!
const forToast = (detail: string): string =>
  detail.length > MAX_DETAIL_LENGTH ? `${detail.slice(0, MAX_DETAIL_LENGTH - 1)}…` : detail;

const crashText = (detail: string): string =>
  isMemoryError(detail) ? MEMORY_CRASH_MESSAGE : `${CRASH_PREFIX} ${forToast(detail)}`;

// Deduped with a Set. Every memory crash shares one message, so the cap effectively only bounds
// the flag-gated traps, whose text varies per trap.
const MAX_CRASH_TOASTS = 3;
const shownCrashMessages = new Set<string>();

/** Test seam: clears the per-page-load toast state. */
export const resetWebViewerErrorState = (): void => {
  shownCrashMessages.clear();
};

interface ReportWebViewerErrorArgs {
  kind: WebViewerErrorKind;
  documentType?: DocumentType;
  error?: unknown;
  /** Explicit message to display; an ErrorEvent often has a null `error` but a populated `message`. */
  message?: string;
}

const isCrashToastSuppressed = (text: string, detail: string): boolean => {
  if (shownCrashMessages.has(text) || shownCrashMessages.size >= MAX_CRASH_TOASTS) return true;
  return !isMemoryError(detail) && !isFullErrorToastsEnabled();
};

/**
 * Single entry point for surfacing a WebViewer failure: shows a persistent error toast with
 * a Reload action and reports to Sentry. Sentry is notified on every call even when the
 * toast is suppressed, so dropping one never costs observability.
 *
 * `init` and `load` always toast — both need a reload. A `crash` only toasts when it points at
 * an exhausted heap, unless `fullWebviewerErrorToastsEnabled` widens it to every engine trap.
 */
export const reportWebViewerError = ({ kind, documentType, error, message }: ReportWebViewerErrorArgs): void => {
  const detail = describeError(error, message);
  const text = kind === 'crash' ? crashText(detail) : MESSAGES[kind];
  const suppressed = kind === 'crash' && isCrashToastSuppressed(text, detail);

  if (!suppressed) {
    if (kind === 'crash') shownCrashMessages.add(text);
    store.dispatch(
      app.actions.setSnackMessage({
        message: text,
        type: 'error',
        autoHideDuration: null,
        // Every one of these failures leaves the viewer unusable, so reloading is the only way out.
        dismissible: false,
        customAction: { label: 'RELOAD PAGE', onClick: () => window.location.reload(), variant: 'standalone' },
      }),
    );
  }

  // An iframe ErrorEvent often has a null `error` while carrying the real text in `message`,
  // and a rejection reason can be any thrown value. Passing those through as-is would report a
  // detail-free "WebViewer crash" or Sentry's unhelpful "Non-Error exception captured".
  const captured = error instanceof Error ? error : new Error(`WebViewer ${kind}: ${detail}`);

  Sentry.captureException(captured, {
    tags: { area: 'webviewer' },
    extra: { kind, documentType, detail },
  });
};
